/**
 * Health sync types and interfaces
 */

// Supported health providers
export enum HealthProvider {
  APPLE_HEALTH = 'apple_health',
  GOOGLE_FIT = 'google_fit',
  NONE = 'none'
}

// Activity types matching original app
export enum ActivityType {
  // Martial Arts
  BOXING = 'boxing',
  KICKBOXING = 'kickboxing',
  WRESTLING = 'wrestling',
  MARTIAL_ARTS = 'martial_arts',
  
  // Exercise Types
  HIIT = 'hiit',
  YOGA = 'yoga',
  RUNNING = 'running',
  SWIMMING = 'swimming',
  CYCLING = 'cycling',
  RESISTANCE = 'resistance',
  CROSS_TRAINING = 'cross_training',
  OTHER = 'other'
}

// Health activity data
export interface HealthActivity {
  type: ActivityType;
  startDate: string; // ISO string
  endDate: string; // ISO string
  duration: number; // seconds
  calories?: number;
  distance?: number; // meters
  notes?: string;
  source?: string; // e.g., 'marune'
}

// Permission status
export enum PermissionStatus {
  NOT_DETERMINED = 'not_determined',
  DENIED = 'denied',
  AUTHORIZED = 'authorized'
}

// Health provider interface
export interface IHealthProvider {
  name: HealthProvider;
  isAvailable(): Promise<boolean>;
  requestPermissions(): Promise<PermissionStatus>;
  checkPermissions(): Promise<PermissionStatus>;
  saveActivity(activity: HealthActivity): Promise<boolean>;
  getActivities(startDate: Date, endDate: Date): Promise<HealthActivity[]>;
}

// Sync metadata
export interface HealthSyncMetadata {
  provider: HealthProvider;
  lastSyncDate?: number;
  syncedItems: {
    [itemId: string]: {
      syncDate: number;
      success: boolean;
      error?: string;
    };
  };
}

// Training type mapping
export const TRAINING_TYPE_TO_ACTIVITY: Record<string, ActivityType> = {
  'boxing': ActivityType.BOXING,
  'kickboxing': ActivityType.KICKBOXING,
  'mma': ActivityType.MARTIAL_ARTS,
  'bjj': ActivityType.MARTIAL_ARTS,
  'judo': ActivityType.MARTIAL_ARTS,
  'karate': ActivityType.MARTIAL_ARTS,
  'muay_thai': ActivityType.KICKBOXING,
  'taekwondo': ActivityType.MARTIAL_ARTS,
  'wrestling': ActivityType.WRESTLING,
  'wwe': ActivityType.WRESTLING,
  'capoeira': ActivityType.MARTIAL_ARTS,
  'krav_maga': ActivityType.MARTIAL_ARTS,
  'kung_fu': ActivityType.MARTIAL_ARTS,
  'aikido': ActivityType.MARTIAL_ARTS,
  'other': ActivityType.OTHER
};

// Exercise type mapping
export const EXERCISE_TYPE_TO_ACTIVITY: Record<string, ActivityType> = {
  'hiit': ActivityType.HIIT,
  'yoga': ActivityType.YOGA,
  'running': ActivityType.RUNNING,
  'swimming': ActivityType.SWIMMING,
  'cycling': ActivityType.CYCLING,
  'resistance': ActivityType.RESISTANCE,
  'crossfit': ActivityType.CROSS_TRAINING,
  'other': ActivityType.OTHER
};